import type {
  DxfLayer,
  DxfPolylineMeta,
  DxfTextLabel,
  FloorPlanBounds,
} from '../types';

export const FLOOR_RASTER_VERSION = 4;
export const FLOOR_RASTER_TILE_PX = 2048;
export const FLOOR_RASTER_OVERVIEW_MAX_PX = 8192;
// Keep enough pyramid depth for close 2D/3D zooms. Visible-tile capping below
// bounds memory, so higher levels improve sharpness without loading a full
// facility-sized texture.
export const FLOOR_RASTER_MAX_LEVEL = 8;
/** Render above display density so line art is minified, never magnified. */
export const FLOOR_RASTER_QUALITY_SCALE = 2.5;

export type RasterWorldBounds = FloorPlanBounds;

export type FloorRasterDataset = {
  sourceId: string;
  polylines: number[][];
  polylineLayers: number[];
  polylineMeta: DxfPolylineMeta[];
  layers: DxfLayer[];
  labels: DxfTextLabel[];
  bounds: FloorPlanBounds;
  hiddenEntityTypes: Record<string, boolean>;
  opacity: number;
  showLabels: boolean;
};

export type FloorRasterDatasetRegistration = {
  datasetId: string;
  contentHash: string;
  visualKey: string;
  bounds: FloorPlanBounds;
  basePixelsPerWorldUnit: number;
};

export type FloorRasterTileAddress = {
  level: number;
  column: number;
  row: number;
};

export type FloorRasterTileSpec = FloorRasterTileAddress & {
  key: string;
  worldBounds: FloorPlanBounds;
  pixelWidth: number;
  pixelHeight: number;
  pixelsPerWorldUnit: number;
};

export type FloorRasterEncodedTile = FloorRasterTileSpec & {
  blob: Blob;
};

export type FloorRasterDecodedTile = FloorRasterTileSpec & {
  image: ImageBitmap;
};

export type FloorRasterViewQuery = {
  worldBounds: FloorPlanBounds;
  pixelsPerWorldUnit: number;
};

const finite = (value: number, fallback: number) =>
  Number.isFinite(value) ? value : fallback;

function fnvString(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/** Visual settings that require a new raster, separate from geometry identity. */
export function floorRasterVisualKey(dataset: FloorRasterDataset): string {
  const layers = dataset.layers
    .map(
      (layer, index) =>
        `${index}:${layer.name}:${layer.color}:${layer.visible === false ? 0 : 1}`
    )
    .join('|');
  const hidden = Object.keys(dataset.hiddenEntityTypes)
    .filter((key) => dataset.hiddenEntityTypes[key])
    .sort()
    .join(',');
  return `v${FLOOR_RASTER_VERSION}-${fnvString(
    `${layers};${hidden};${dataset.showLabels ? 1 : 0};${finite(dataset.opacity, 1).toFixed(3)}`
  )}`;
}

export function floorRasterBasePixelsPerWorldUnit(bounds: FloorPlanBounds): number {
  const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1);
  return Math.min(2.5, FLOOR_RASTER_OVERVIEW_MAX_PX / span);
}

export function floorRasterPixelsPerWorldUnit(
  bounds: FloorPlanBounds,
  level: number
): number {
  return floorRasterBasePixelsPerWorldUnit(bounds) * 2 ** Math.max(0, level);
}

export function floorRasterLevelForDensity(
  bounds: FloorPlanBounds,
  requestedPixelsPerWorldUnit: number
): number {
  const base = floorRasterBasePixelsPerWorldUnit(bounds);
  const ratio = Math.max(1, finite(requestedPixelsPerWorldUnit, base) / base);
  return Math.min(FLOOR_RASTER_MAX_LEVEL, Math.max(0, Math.ceil(Math.log2(ratio))));
}

/** Keep 3D requests on the look-at window so a horizon ray cannot force overview-only tiles. */
export function floorRasterFocusBounds(
  frustum: FloorPlanBounds,
  focusX: number,
  focusY: number,
  maxHalfSpan: number
): FloorPlanBounds {
  const half = Math.max(finite(maxHalfSpan, 1), 1);
  const focused = {
    minX: focusX - half,
    minY: focusY - half,
    maxX: focusX + half,
    maxY: focusY + half,
  };
  return intersectRasterBounds(frustum, focused) ?? frustum;
}

export function intersectRasterBounds(
  a: FloorPlanBounds,
  b: FloorPlanBounds
): FloorPlanBounds | null {
  const minX = Math.max(a.minX, b.minX);
  const minY = Math.max(a.minY, b.minY);
  const maxX = Math.min(a.maxX, b.maxX);
  const maxY = Math.min(a.maxY, b.maxY);
  return maxX > minX && maxY > minY ? { minX, minY, maxX, maxY } : null;
}

/**
 * Deterministic tile grid in normalized floor-plan world coordinates.
 * Every level uses the same origin, so changing LOD cannot move the drawing.
 */
export function floorRasterTileSpecs(
  registration: FloorRasterDatasetRegistration,
  level: number,
  visibleBounds: FloorPlanBounds = registration.bounds
): FloorRasterTileSpec[] {
  const clampedLevel = Math.min(FLOOR_RASTER_MAX_LEVEL, Math.max(0, level));
  const density = registration.basePixelsPerWorldUnit * 2 ** clampedLevel;
  const tileWorldSize = FLOOR_RASTER_TILE_PX / density;
  const clipped = intersectRasterBounds(registration.bounds, visibleBounds);
  if (!clipped) return [];

  const floor = registration.bounds;
  const firstColumn = Math.max(0, Math.floor((clipped.minX - floor.minX) / tileWorldSize));
  const firstRow = Math.max(0, Math.floor((clipped.minY - floor.minY) / tileWorldSize));
  const lastColumn = Math.max(
    firstColumn,
    Math.ceil((clipped.maxX - floor.minX) / tileWorldSize) - 1
  );
  const lastRow = Math.max(
    firstRow,
    Math.ceil((clipped.maxY - floor.minY) / tileWorldSize) - 1
  );

  const specs: FloorRasterTileSpec[] = [];
  for (let row = firstRow; row <= lastRow; row += 1) {
    for (let column = firstColumn; column <= lastColumn; column += 1) {
      const minX = floor.minX + column * tileWorldSize;
      const minY = floor.minY + row * tileWorldSize;
      const maxX = Math.min(floor.maxX, minX + tileWorldSize);
      const maxY = Math.min(floor.maxY, minY + tileWorldSize);
      specs.push({
        level: clampedLevel,
        column,
        row,
        key: `${registration.contentHash}|${registration.visualKey}|${clampedLevel}|${column}|${row}`,
        worldBounds: { minX, minY, maxX, maxY },
        pixelWidth: Math.max(1, Math.ceil((maxX - minX) * density)),
        pixelHeight: Math.max(1, Math.ceil((maxY - minY) * density)),
        pixelsPerWorldUnit: density,
      });
    }
  }
  return specs;
}

/**
 * Selects the sharpest tile level that fits a bounded visible working set.
 * A perspective camera near the horizon can intersect most of the facility;
 * blindly honoring its pixel density would otherwise pin hundreds of decoded
 * textures and defeat the memory budget.
 */
export function floorRasterCappedTileSpecs(
  registration: FloorRasterDatasetRegistration,
  requestedLevel: number,
  visibleBounds: FloorPlanBounds,
  maxTiles = 48
): FloorRasterTileSpec[] {
  const clampedTarget = Math.min(FLOOR_RASTER_MAX_LEVEL, Math.max(0, requestedLevel));
  for (let level = clampedTarget; level >= 0; level -= 1) {
    const specs = floorRasterTileSpecs(registration, level, visibleBounds);
    if (specs.length === 0) continue;
    if (specs.length <= maxTiles) return specs;
    if (level === clampedTarget && specs.length <= maxTiles * 1.5) {
      const cx = (visibleBounds.minX + visibleBounds.maxX) / 2;
      const cy = (visibleBounds.minY + visibleBounds.maxY) / 2;
      return specs
        .sort((a, b) => {
          const ax = (a.worldBounds.minX + a.worldBounds.maxX) / 2;
          const ay = (a.worldBounds.minY + a.worldBounds.maxY) / 2;
          const bx = (b.worldBounds.minX + b.worldBounds.maxX) / 2;
          const by = (b.worldBounds.minY + b.worldBounds.maxY) / 2;
          return (ax - cx) ** 2 + (ay - cy) ** 2 - ((bx - cx) ** 2 + (by - cy) ** 2);
        })
        .slice(0, maxTiles);
    }
  }
  const specs = floorRasterTileSpecs(
    registration,
    Math.max(0, requestedLevel),
    visibleBounds
  );
  if (specs.length === 0) return [];
  if (specs.length <= maxTiles) return specs;
  const cx = (visibleBounds.minX + visibleBounds.maxX) / 2;
  const cy = (visibleBounds.minY + visibleBounds.maxY) / 2;
  return specs
    .sort((a, b) => {
      const ax = (a.worldBounds.minX + a.worldBounds.maxX) / 2;
      const ay = (a.worldBounds.minY + a.worldBounds.maxY) / 2;
      const bx = (b.worldBounds.minX + b.worldBounds.maxX) / 2;
      const by = (b.worldBounds.minY + b.worldBounds.maxY) / 2;
      return (ax - cx) ** 2 + (ay - cy) ** 2 - ((bx - cx) ** 2 + (by - cy) ** 2);
    })
    .slice(0, maxTiles);
}

export function floorRasterWorldToPixel(
  tile: FloorRasterTileSpec,
  x: number,
  y: number
): { x: number; y: number } {
  return {
    x: (x - tile.worldBounds.minX) * tile.pixelsPerWorldUnit,
    y: (y - tile.worldBounds.minY) * tile.pixelsPerWorldUnit,
  };
}

/** Three.js X/Z plane placement corresponding exactly to a canvas X/Y tile. */
export function floorRasterTilePlane(tile: FloorRasterTileSpec): {
  position: [number, number, number];
  size: [number, number];
} {
  const bounds = tile.worldBounds;
  return {
    position: [
      (bounds.minX + bounds.maxX) / 2,
      0.015,
      (bounds.minY + bounds.maxY) / 2,
    ],
    size: [bounds.maxX - bounds.minX, bounds.maxY - bounds.minY],
  };
}

